/**
 * Main Widget class with Shadow DOM isolation
 */

import type { SiteGPTWidgetConfig, BotConfig, Message, LeadData } from './types/config';
import { ApiService } from './services/api';
import * as storage from './services/storage';
import { BASE_STYLES, generateThemeStyles } from './styles/base';
import { ICONS } from './utils/icons';
import { createElement, setInnerHTML, formatMessage, scrollToBottom, autoResizeTextarea } from './utils/dom';

export class Widget {
  private config: SiteGPTWidgetConfig;
  private api: ApiService;
  private container: HTMLElement;
  private shadow: ShadowRoot;
  private botConfig: BotConfig | null = null;
  private conversationId: string | null = null;
  private leadId: string | null = null;
  private messages: Message[] = [];
  private isOpen = false;
  private isLoading = false;
  private isTyping = false;
  private showLeadForm = true;

  // DOM references
  private bubble: HTMLElement | null = null;
  private window: HTMLElement | null = null;
  private messagesContainer: HTMLElement | null = null;
  private input: HTMLTextAreaElement | null = null;
  private sendButton: HTMLButtonElement | null = null;
  private welcomeCloud: HTMLElement | null = null;
  private welcomeCloudTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(config: SiteGPTWidgetConfig) {
    this.config = {
      position: 'bottom-right',
      offsetX: 20,
      offsetY: 20,
      openOnLoad: false,
      showLeadForm: true,
      leadFormFields: ['name', 'email'],
      debug: false,
      ...config,
    };

    this.api = new ApiService(
      config.botId,
      config.apiBaseUrl,
      config.debug
    );

    // Create container with Shadow DOM
    this.container = document.createElement('div');
    this.container.id = 'sitegpt-widget-root';
    this.shadow = this.container.attachShadow({ mode: 'closed' });
  }

  private log(...args: unknown[]): void {
    if (this.config.debug) {
      console.log('[SiteGPT Widget]', ...args);
    }
  }

  /**
   * Mount the widget to the DOM
   */
  async mount(): Promise<void> {
    this.log('Mounting widget...');

    try {
      // Restore state from storage
      this.conversationId = storage.getConversationId(this.config.botId);
      this.leadId = storage.getLeadId(this.config.botId);
      this.messages = storage.getMessages(this.config.botId).map(m => ({
        id: m.id,
        sendBy: m.sendBy,
        message: m.message,
        createdAt: m.createdAt,
      }));
      this.showLeadForm = this.config.showLeadForm !== false && !this.leadId;

      // Fetch bot configuration
      this.botConfig = await this.api.getBotConfig();
      this.log('Bot config loaded:', this.botConfig);

      // If we have a stored conversation, verify it exists on server
      if (this.conversationId) {
        this.log('Verifying stored conversation exists...', this.conversationId);
        const conversationExists = await this.api.checkConversationExists(this.conversationId);

        if (!conversationExists) {
          this.log('Stored conversation not found on server, resetting...');
          this.resetConversation();
        } else if (this.messages.length === 0) {
          // Conversation exists but no local messages, try to restore from server
          try {
            const serverMessages = await this.api.getConversationMessages(this.conversationId);
            this.messages = serverMessages;
            storage.setMessages(this.config.botId, serverMessages.map(m => ({
              id: m.id,
              sendBy: m.sendBy,
              message: m.message,
              createdAt: m.createdAt,
            })));
            this.log('Restored messages from server:', this.messages.length);
          } catch (e) {
            this.log('Failed to restore messages:', e);
            // Clear invalid conversation
            this.resetConversation();
          }
        }
      }

      // Inject styles
      this.injectStyles();

      // Render UI
      this.render();

      // Append to body
      document.body.appendChild(this.container);

      // Open on load if configured
      if (this.config.openOnLoad) {
        this.open();
      } else if (!this.config.disableWelcomeCloud) {
        // Show welcome cloud if chat is not opening immediately
        this.showWelcomeCloud();
      }

      this.log('Widget mounted successfully');
    } catch (error) {
      this.log('Failed to mount widget:', error);
      this.config.onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  }

  /**
   * Unmount the widget from the DOM
   */
  unmount(): void {
    this.log('Unmounting widget...');
    this.hideWelcomeCloud();
    this.container.remove();
  }

  /**
   * Open the chat window
   */
  open(): void {
    if (this.isOpen) return;

    this.isOpen = true;
    this.hideWelcomeCloud();
    this.window?.classList.add('open');
    storage.setWidgetState(this.config.botId, true);
    this.config.onOpen?.();

    // Focus input if not showing lead form
    if (!this.showLeadForm) {
      setTimeout(() => this.input?.focus(), 100);
    }

    // Scroll to bottom
    if (this.messagesContainer) {
      scrollToBottom(this.messagesContainer);
    }
  }

  /**
   * Close the chat window
   */
  close(): void {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.window?.classList.remove('open');
    storage.setWidgetState(this.config.botId, false);
    this.config.onClose?.();
  }

  /**
   * Toggle the chat window
   */
  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Reset the conversation - clears all messages and starts fresh
   */
  resetConversation(): void {
    this.log('Resetting conversation...');

    // Clear local state
    this.conversationId = null;
    this.messages = [];

    // Clear storage
    storage.clearConversation(this.config.botId);

    // Re-render if already mounted
    if (this.container.parentNode) {
      // Store current open state before render
      const wasOpen = this.isOpen;
      this.render();
      // Restore the open class on the new window element if it was open
      if (wasOpen) {
        this.window?.classList.add('open');
        // Focus the input after reset
        setTimeout(() => this.input?.focus(), 100);
      }
    }

    this.log('Conversation reset complete');
  }

  /**
   * Show a confirmation dialog inside the widget
   */
  private showConfirmDialog(
    title: string,
    message: string,
    onConfirm: () => void,
    onCancel?: () => void
  ): void {
    // Create overlay
    const overlay = createElement('div', { className: 'sitegpt-confirm-overlay' });

    // Create dialog
    const dialog = createElement('div', { className: 'sitegpt-confirm-dialog' });

    // Title
    const titleEl = createElement('h3', { className: 'sitegpt-confirm-title' }, [title]);
    dialog.appendChild(titleEl);

    // Message
    const messageEl = createElement('p', { className: 'sitegpt-confirm-message' }, [message]);
    dialog.appendChild(messageEl);

    // Buttons container
    const buttons = createElement('div', { className: 'sitegpt-confirm-buttons' });

    // Cancel button
    const cancelBtn = createElement('button', { className: 'sitegpt-confirm-btn cancel' }, ['Cancel']);
    cancelBtn.addEventListener('click', () => {
      overlay.remove();
      onCancel?.();
    });
    buttons.appendChild(cancelBtn);

    // Confirm button
    const confirmBtn = createElement('button', { className: 'sitegpt-confirm-btn confirm' }, ['Confirm']);
    confirmBtn.addEventListener('click', () => {
      overlay.remove();
      onConfirm();
    });
    buttons.appendChild(confirmBtn);

    dialog.appendChild(buttons);
    overlay.appendChild(dialog);

    // Close on overlay click (outside dialog)
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        overlay.remove();
        onCancel?.();
      }
    });

    // Append to window
    this.window?.appendChild(overlay);
  }

  /**
   * Inject styles into Shadow DOM
   */
  private injectStyles(): void {
    const style = document.createElement('style');
    style.textContent = BASE_STYLES;

    // Add theme overrides
    const primaryColor = this.config.primaryColor || this.botConfig?.appearance?.color;
    if (primaryColor) {
      style.textContent += generateThemeStyles(primaryColor);
    }

    // Add position overrides
    const { position, offsetX, offsetY } = this.config;
    style.textContent += `
      .sitegpt-bubble.${position} {
        ${position === 'bottom-right' ? 'right' : 'left'}: ${offsetX}px;
        bottom: ${offsetY}px;
      }
      .sitegpt-window.${position} {
        ${position === 'bottom-right' ? 'right' : 'left'}: ${offsetX}px;
        bottom: ${(offsetY || 20) + 70}px;
      }
    `;

    this.shadow.appendChild(style);
  }

  /**
   * Render the widget UI
   */
  private render(): void {
    // Clear existing content
    const existingContent = this.shadow.querySelectorAll(':not(style)');
    existingContent.forEach(el => el.remove());

    // Create bubble button
    this.bubble = this.createBubble();
    this.shadow.appendChild(this.bubble);

    // Create chat window
    this.window = this.createWindow();
    this.shadow.appendChild(this.window);
  }

  /**
   * Create the floating bubble button
   */
  private createBubble(): HTMLElement {
    const bubble = createElement('button', {
      className: `sitegpt-bubble ${this.config.position}`,
      'aria-label': 'Open chat',
    });

    // Use custom icon or default
    const iconUrl = this.config.botIcon || this.botConfig?.appearance?.bot_icon;
    if (iconUrl) {
      const img = createElement('img', {
        src: iconUrl,
        alt: 'Chat',
        className: 'sitegpt-bubble-icon',
      });
      bubble.appendChild(img);
    } else {
      setInnerHTML(bubble, ICONS.chat);
    }

    bubble.addEventListener('click', () => this.toggle());

    return bubble;
  }

  /**
   * Create the welcome cloud element
   */
  private createWelcomeCloud(): HTMLElement {
    const cloud = createElement('div', {
      className: `sitegpt-welcome-cloud ${this.config.position}`,
    });

    cloud.textContent = this.getWelcomeMessage();

    cloud.addEventListener('click', () => {
      this.hideWelcomeCloud();
      this.open();
    });

    return cloud;
  }

  /**
   * Get the welcome message (from server or default)
   */
  private getWelcomeMessage(): string {
    return this.botConfig?.appearance?.welcome_message ||
      'Hello! How can I help you today?';
  }

  /**
   * Show the welcome cloud bubble
   */
  private showWelcomeCloud(): void {
    if (this.welcomeCloud) return;

    // Delay showing the cloud to let the widget fully initialize
    this.welcomeCloudTimeout = setTimeout(() => {
      // Check again in case chat was opened during the delay
      if (this.isOpen || this.welcomeCloud) return;

      this.welcomeCloud = this.createWelcomeCloud();
      this.shadow.appendChild(this.welcomeCloud);
      this.log('Welcome cloud shown');

      // Auto-hide after 5 seconds
      this.welcomeCloudTimeout = setTimeout(() => {
        this.hideWelcomeCloud();
      }, 5000);
    }, 1500);
  }

  /**
   * Hide the welcome cloud bubble
   */
  private hideWelcomeCloud(): void {
    if (!this.welcomeCloud) return;

    if (this.welcomeCloudTimeout) {
      clearTimeout(this.welcomeCloudTimeout);
      this.welcomeCloudTimeout = null;
    }

    this.welcomeCloud.classList.add('hiding');
    this.welcomeCloud.addEventListener('animationend', () => {
      this.welcomeCloud?.remove();
      this.welcomeCloud = null;
      this.log('Welcome cloud hidden');
    }, { once: true });
  }

  /**
   * Create the chat window
   */
  private createWindow(): HTMLElement {
    const window = createElement('div', {
      className: `sitegpt-window ${this.config.position}`,
    });

    // Header
    window.appendChild(this.createHeader());

    // Content (lead form or messages)
    if (this.showLeadForm) {
      window.appendChild(this.createLeadForm());
    } else {
      window.appendChild(this.createMessagesArea());
      window.appendChild(this.createInputArea());
    }

    // Powered by footer
    window.appendChild(this.createPoweredBy());

    return window;
  }

  /**
   * Create the header
   */
  private createHeader(): HTMLElement {
    const header = createElement('div', { className: 'sitegpt-header' });

    // Icon
    const iconContainer = createElement('div', { className: 'sitegpt-header-icon' });
    const iconUrl = this.config.botIcon || this.botConfig?.appearance?.bot_icon;
    if (iconUrl) {
      const img = createElement('img', { src: iconUrl, alt: '' });
      iconContainer.appendChild(img);
    } else {
      setInnerHTML(iconContainer, ICONS.bot);
    }
    header.appendChild(iconContainer);

    // Info
    const info = createElement('div', { className: 'sitegpt-header-info' });
    const title = createElement('h2', { className: 'sitegpt-header-title' }, [
      this.botConfig?.botName || 'Chat Assistant',
    ]);
    const subtitle = createElement('p', { className: 'sitegpt-header-subtitle' }, [
      'Online',
    ]);
    info.appendChild(title);
    info.appendChild(subtitle);
    header.appendChild(info);

    // Header buttons container
    const headerButtons = createElement('div', { className: 'sitegpt-header-buttons' });

    // Reset button
    const resetBtn = createElement('button', {
      className: 'sitegpt-header-btn',
      'aria-label': 'New conversation',
      title: 'Start new conversation',
    });
    setInnerHTML(resetBtn, ICONS.refresh);
    resetBtn.addEventListener('click', () => {
      this.showConfirmDialog(
        'New Conversation',
        'Start a new conversation? This will clear the current chat history.',
        () => this.resetConversation()
      );
    });
    headerButtons.appendChild(resetBtn);

    // Close button
    const closeBtn = createElement('button', {
      className: 'sitegpt-header-btn sitegpt-header-close',
      'aria-label': 'Close chat',
    });
    setInnerHTML(closeBtn, ICONS.close);
    closeBtn.addEventListener('click', () => this.close());
    headerButtons.appendChild(closeBtn);

    header.appendChild(headerButtons);

    return header;
  }

  /**
   * Create the lead capture form
   */
  private createLeadForm(): HTMLElement {
    const form = createElement('div', { className: 'sitegpt-lead-form' });

    const heading = createElement('h3', {}, ['Start a conversation']);
    const description = createElement('p', {}, [
      'Please provide your details to begin.',
    ]);
    form.appendChild(heading);
    form.appendChild(description);

    const fields = this.config.leadFormFields || ['name', 'email'];
    const inputs: Record<string, HTMLInputElement> = {};

    // Name field
    if (fields.includes('name')) {
      const field = createElement('div', { className: 'sitegpt-form-field' });
      const label = createElement('label', { for: 'sitegpt-name' }, ['Name *']);
      const input = createElement('input', {
        type: 'text',
        id: 'sitegpt-name',
        placeholder: 'Your name',
      }) as HTMLInputElement;
      field.appendChild(label);
      field.appendChild(input);
      form.appendChild(field);
      inputs.name = input;
    }

    // Email field
    if (fields.includes('email')) {
      const field = createElement('div', { className: 'sitegpt-form-field' });
      const label = createElement('label', { for: 'sitegpt-email' }, ['Email']);
      const input = createElement('input', {
        type: 'email',
        id: 'sitegpt-email',
        placeholder: 'your@email.com',
      }) as HTMLInputElement;
      field.appendChild(label);
      field.appendChild(input);
      form.appendChild(field);
      inputs.email = input;
    }

    // Phone field
    if (fields.includes('phone')) {
      const field = createElement('div', { className: 'sitegpt-form-field' });
      const label = createElement('label', { for: 'sitegpt-phone' }, ['Phone']);
      const input = createElement('input', {
        type: 'tel',
        id: 'sitegpt-phone',
        placeholder: 'Your phone number',
      }) as HTMLInputElement;
      field.appendChild(label);
      field.appendChild(input);
      form.appendChild(field);
      inputs.phone = input;
    }

    // Submit button
    const submitBtn = createElement('button', {
      className: 'sitegpt-form-submit',
    }, ['Start Chat']) as HTMLButtonElement;

    submitBtn.addEventListener('click', async () => {
      const name = inputs.name?.value.trim() || '';
      const email = inputs.email?.value.trim();
      const phone = inputs.phone?.value.trim();

      if (!name) {
        inputs.name?.focus();
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = 'Starting...';

      try {
        const leadData: LeadData = { name, email, phone };
        this.leadId = await this.api.createLead(leadData);
        storage.setLeadId(this.config.botId, this.leadId);
        this.showLeadForm = false;
        this.config.onLeadCaptured?.(leadData);
        // Store current open state before render
        const wasOpen = this.isOpen;
        this.render();
        // Restore the open class on the new window element if it was open
        if (wasOpen) {
          this.window?.classList.add('open');
          // Focus the input after transitioning to chat
          setTimeout(() => this.input?.focus(), 100);
        }
      } catch (error) {
        this.log('Failed to create lead:', error);
        submitBtn.disabled = false;
        submitBtn.textContent = 'Start Chat';
      }
    });

    form.appendChild(submitBtn);

    // Skip link
    const skip = createElement('p', { className: 'sitegpt-form-skip' });
    const skipBtn = createElement('button', {}, ['Skip for now']);
    skipBtn.addEventListener('click', () => {
      this.showLeadForm = false;
      // Store current open state before render
      const wasOpen = this.isOpen;
      this.render();
      // Restore the open class on the new window element if it was open
      if (wasOpen) {
        this.window?.classList.add('open');
        // Focus the input after transitioning to chat
        setTimeout(() => this.input?.focus(), 100);
      }
    });
    skip.appendChild(skipBtn);
    form.appendChild(skip);

    return form;
  }

  /**
   * Create the messages area
   */
  private createMessagesArea(): HTMLElement {
    this.messagesContainer = createElement('div', { className: 'sitegpt-messages' });

    // Welcome message
    const welcomeMessage = this.config.welcomeMessage ||
      this.botConfig?.appearance?.welcome_message ||
      'Hello! How can I help you today?';

    if (this.messages.length === 0) {
      const welcome = createElement('div', { className: 'sitegpt-welcome' }, [
        welcomeMessage,
      ]);
      this.messagesContainer.appendChild(welcome);
    }

    // Render existing messages
    for (const msg of this.messages) {
      this.renderMessage(msg);
    }

    return this.messagesContainer;
  }

  /**
   * Create the input area
   */
  private createInputArea(): HTMLElement {
    const inputArea = createElement('div', { className: 'sitegpt-input-area' });

    // Textarea
    this.input = createElement('textarea', {
      className: 'sitegpt-input',
      placeholder: 'Type a message...',
      rows: '1',
    }) as HTMLTextAreaElement;

    this.input.addEventListener('input', () => {
      if (this.input) {
        autoResizeTextarea(this.input);
      }
    });

    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    });

    inputArea.appendChild(this.input);

    // Send button
    this.sendButton = createElement('button', {
      className: 'sitegpt-send',
      'aria-label': 'Send message',
    }) as HTMLButtonElement;
    setInnerHTML(this.sendButton, ICONS.send);

    this.sendButton.addEventListener('click', () => this.sendMessage());

    inputArea.appendChild(this.sendButton);

    return inputArea;
  }

  /**
   * Create powered by footer
   */
  private createPoweredBy(): HTMLElement {
    const footer = createElement('div', { className: 'sitegpt-powered' });
    footer.innerHTML = 'Powered by <a href="https://site-gpt.com" target="_blank" rel="noopener">SiteGPT</a>';
    return footer;
  }

  /**
   * Render a message to the messages container
   */
  private renderMessage(msg: Message): void {
    if (!this.messagesContainer) return;

    // Remove welcome message if present
    const welcome = this.messagesContainer.querySelector('.sitegpt-welcome');
    if (welcome) {
      welcome.remove();
    }

    const messageEl = createElement('div', {
      className: `sitegpt-message ${msg.sendBy}`,
    });

    // Use innerHTML for bot messages (supports basic formatting)
    if (msg.sendBy === 'bot') {
      messageEl.innerHTML = formatMessage(msg.message);
    } else {
      messageEl.textContent = msg.message;
    }

    this.messagesContainer.appendChild(messageEl);
    scrollToBottom(this.messagesContainer);
  }

  /**
   * Show typing indicator
   */
  private showTypingIndicator(): void {
    if (!this.messagesContainer || this.isTyping) return;

    this.isTyping = true;

    const typing = createElement('div', { className: 'sitegpt-typing' });
    typing.id = 'sitegpt-typing-indicator';
    for (let i = 0; i < 3; i++) {
      typing.appendChild(createElement('span', { className: 'sitegpt-typing-dot' }));
    }

    this.messagesContainer.appendChild(typing);
    scrollToBottom(this.messagesContainer);
  }

  /**
   * Hide typing indicator
   */
  private hideTypingIndicator(): void {
    this.isTyping = false;
    const typing = this.messagesContainer?.querySelector('#sitegpt-typing-indicator');
    typing?.remove();
  }

  /**
   * Send a message
   */
  private async sendMessage(): Promise<void> {
    if (!this.input || this.isLoading) return;

    const message = this.input.value.trim();
    if (!message) return;

    // Clear input
    this.input.value = '';
    autoResizeTextarea(this.input);

    // Disable send button
    this.isLoading = true;
    if (this.sendButton) {
      this.sendButton.disabled = true;
    }

    // Add user message
    const userMsg: Message = {
      id: `temp-${Date.now()}`,
      sendBy: 'user',
      message,
      createdAt: new Date().toISOString(),
    };
    this.messages.push(userMsg);
    storage.addMessage(this.config.botId, {
      id: userMsg.id,
      sendBy: userMsg.sendBy,
      message: userMsg.message,
      createdAt: userMsg.createdAt,
    });
    this.renderMessage(userMsg);
    this.config.onMessageSent?.(message);

    // Show typing indicator
    this.showTypingIndicator();

    // Prepare bot message placeholder
    const botMsg: Message = {
      id: `temp-bot-${Date.now()}`,
      sendBy: 'bot',
      message: '',
      createdAt: new Date().toISOString(),
    };

    try {
      await this.api.sendMessageStreaming(
        message,
        this.conversationId,
        this.leadId,
        // On chunk
        (chunk) => {
          this.hideTypingIndicator();

          botMsg.message += chunk;

          // Update or create bot message element
          let botMsgEl = this.messagesContainer?.querySelector(`[data-msg-id="${botMsg.id}"]`);
          if (!botMsgEl) {
            botMsgEl = createElement('div', {
              className: 'sitegpt-message bot',
              'data-msg-id': botMsg.id,
            });
            this.messagesContainer?.appendChild(botMsgEl);
          }
          botMsgEl.innerHTML = formatMessage(botMsg.message);

          if (this.messagesContainer) {
            scrollToBottom(this.messagesContainer);
          }
        },
        // On complete
        (fullResponse, newConversationId) => {
          this.hideTypingIndicator();

          // Update conversation ID
          if (newConversationId && newConversationId !== this.conversationId) {
            this.conversationId = newConversationId;
            storage.setConversationId(this.config.botId, newConversationId);
          }

          // Save bot message
          botMsg.message = fullResponse;
          this.messages.push(botMsg);
          storage.addMessage(this.config.botId, {
            id: botMsg.id,
            sendBy: botMsg.sendBy,
            message: botMsg.message,
            createdAt: botMsg.createdAt,
          });

          // Re-enable input
          this.isLoading = false;
          if (this.sendButton) {
            this.sendButton.disabled = false;
          }
          this.input?.focus();
        },
        // On error
        (error) => {
          this.hideTypingIndicator();
          this.log('Message error:', error);

          // Show error message
          const errorEl = createElement('div', { className: 'sitegpt-error' }, [
            'Sorry, something went wrong. Please try again.',
          ]);
          this.messagesContainer?.appendChild(errorEl);

          // Remove error after 5 seconds
          setTimeout(() => errorEl.remove(), 5000);

          // Re-enable input
          this.isLoading = false;
          if (this.sendButton) {
            this.sendButton.disabled = false;
          }

          this.config.onError?.(error);
        }
      );
    } catch (error) {
      this.hideTypingIndicator();
      this.isLoading = false;
      if (this.sendButton) {
        this.sendButton.disabled = false;
      }
      this.config.onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  }
}
